import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableSortLabel,
} from '@mui/material';
import {
  PencilSimple,
  Plus,
  DotsThreeVertical,
  X,
  Copy,
} from '@phosphor-icons/react';
import {
  ScreenWrap,
  HeaderBreadcrumbs,
  SoloLoading,
  ScreenError,
} from '../../components';
import { useParams } from 'react-router';
import { useState } from 'react';
import { Api, useApiCallable } from '../../api';
import { toastErrorHandler } from '../../util';
import useModifiableParams from '../../hooks/use-modifiable-params';
import NewTeamModal from './NewTeamModal';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import MoveLicencesModal from './components/MoveLicencesModal';
import EditExpiryModal from './components/EditExpiryModal';
import { Model } from '../../models';

export default function TeamDetailsScreen() {
  const { id, teamId } = useParams();

  if (!id || !teamId) {
    throw new Error('No orgId or teamId');
  }

  return <TeamDetailsLoaded orgId={id} teamId={teamId} />;
}

type Props = {
  orgId: string;
  teamId: string;
};

function TeamDetailsLoaded({ orgId, teamId }: Props) {
  const { getParams, tablePaginationProps, tableSortLabelProps } =
    useModifiableParams();
  const params = getParams();
  const [selectedLicenses, setSelectedLicenses] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(
    null,
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [editExpiryModalOpen, setEditExpiryModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [numberOfLicences, setNumberOfLicences] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [pendingLicenceIds, setPendingLicenceIds] = useState<string[]>([]);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  const orgApi = Api.Organisation.useReadItem(orgId);
  const teamsApi = Api.OrganisationTeam.useReadCollectionOrganisationSub(
    orgId,
    {},
  );

  const licencesApi = Api.TeamLicence.useReadCollectionLicences(teamId, params);
  const callApi = useApiCallable();

  if (orgApi.error) return <ScreenError {...orgApi} />;
  if (licencesApi.error) return <ScreenError {...licencesApi} />;
  if (teamsApi.error) return <ScreenError {...teamsApi} />;
  if (!orgApi.data || !licencesApi.data || !teamsApi.data)
    return <SoloLoading />;

  const organisation = orgApi.data;
  const team = teamsApi.data.member.find((t) => t.id === teamId);
  if (!team) return <div>Team not found</div>;
  const otherTeams = teamsApi.data.member.filter((t) => t.id !== teamId);

  const licences = licencesApi.data.member;

  const getActiveLicenceIds = () =>
    selectedLicenses.length > 0
      ? selectedLicenses
      : selectedLicenseId
        ? [selectedLicenseId]
        : [];

  async function revoke(licenceIds: string[]) {
    const res = await callApi(
      `/teams/${teamId}/licence-update`,
      { method: 'POST', jsonBody: { licenceIds, revoke: true } },
      { errorHandler: toastErrorHandler },
    );
    if (res) {
      licencesApi.invalidate();
      toast.success('Licence(s) revoked');
      return true;
    }
    return false;
  }

  async function move(licenceIds: string[], teamIri: string) {
    const res = await callApi(
      `/teams/${teamId}/licence-update`,
      { method: 'POST', jsonBody: { licenceIds, team: teamIri } },
      { errorHandler: toastErrorHandler },
    );
    if (res) {
      licencesApi.invalidate();
      toast.success('Licence(s) moved to selected team');
      return true;
    }
    return false;
  }

  async function setExpiry(licenceIds: string[], expiresAtIso: string) {
    const res = await callApi(
      `/teams/${teamId}/licence-update`,
      { method: 'POST', jsonBody: { licenceIds, expiresAt: expiresAtIso } },
      { errorHandler: toastErrorHandler },
    );
    if (res) {
      licencesApi.invalidate();
      toast.success('Expiry date updated');
      return true;
    }
    return false;
  }

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedLicenseId(null);
  };

  const handleRevoke = async () => {
    const ids = getActiveLicenceIds();
    if (ids.length === 0) return;
    handleCloseMenu();
    const res = await revoke(ids);
    if (res) {
      setSelectedLicenses([]);
      setSelectedLicenseId(null);
    }
  };

  const handleMove = () => {
    setPendingLicenceIds(getActiveLicenceIds());
    handleCloseMenu();
    if (otherTeams.length === 1) setSelectedTeam(otherTeams[0]['@id']);
    setMoveModalOpen(true);
  };

  const handleEdit = () => {
    const ids = getActiveLicenceIds();
    setPendingLicenceIds(ids);
    if (ids.length > 0 && licencesApi.data) {
      const expiresAt = licencesApi.data.member.find(
        (l) => l.id === ids[0],
      )?.expiresAt;
      if (expiresAt) setExpiryDate(expiresAt);
    }
    handleCloseMenu();
    setEditExpiryModalOpen(true);
  };

  const handleCreateLicences = async () => {
    const count = parseInt(numberOfLicences, 10);
    if (!count || count <= 0) return;
    if (!expiryDate) return;

    const response = await callApi(
      `/organisation-teams/${teamId}/add-licences`,
      {
        method: 'POST',
        jsonBody: {
          noOfLicences: count,
          licenceExpiresAt: expiryDate,
        },
      },
      { errorHandler: toastErrorHandler },
    );

    if (response) {
      const returnedCodes = Array.isArray(
        (response.data as any)?.generatedCodes,
      )
        ? ((response.data as any).generatedCodes as string[])
        : [];
      setGeneratedCodes(returnedCodes);
      setCreateModalOpen(false);
      setSuccessModalOpen(true);
      licencesApi.invalidate();
    }
  };

  const programHasStarted = parseISO(team.programStartDate) <= new Date();

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <HeaderBreadcrumbs
          title={`${organisation.name}: ${team.name}`}
          items={[
            { name: 'B2B', url: '/b2b' },
            { name: organisation.name, url: `/b2b/${organisation.id}` },
            { name: 'Team' },
          ]}
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body1">
              Coach: {team.coach.name} | Program Start Date:{' '}
              {format(team.programStartDate, 'dd MMM yyyy')}
            </Typography>
            <Button
              variant="text"
              startIcon={<PencilSimple />}
              size="small"
              onClick={() => setTeamModalOpen(true)}
            >
              Edit
            </Button>
          </Stack>
          {selectedLicenses.length > 0 ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedLicenses.length} selected
              </Typography>
              <Button variant="outlined" onClick={handleRevoke}>
                Revoke Licences
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPendingLicenceIds(selectedLicenses);
                  if (selectedLicenses.length > 0) {
                    const expiresAt = licences.find(
                      (l) => l.id === selectedLicenses[0],
                    )?.expiresAt;
                    if (expiresAt) setExpiryDate(expiresAt);
                  }
                  setEditExpiryModalOpen(true);
                }}
              >
                Edit Expiry Date
              </Button>
              {!programHasStarted && (
                <Button
                  variant="outlined"
                  disabled={programHasStarted}
                  onClick={() => {
                    setPendingLicenceIds(selectedLicenses);
                    if (otherTeams.length === 1)
                      setSelectedTeam(otherTeams[0]['@id']);
                    setMoveModalOpen(true);
                  }}
                >
                  Move to other team
                </Button>
              )}
            </Stack>
          ) : (
            <Button
              variant="contained"
              startIcon={<Plus />}
              onClick={() => setCreateModalOpen(true)}
            >
              Create Licences
            </Button>
          )}
        </Stack>

        <Card>
          {licences && licences.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedLicenses.length > 0 &&
                          selectedLicenses.length < licences.length
                        }
                        checked={
                          licences.length > 0 &&
                          selectedLicenses.length === licences.length
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedLicenses(
                              licences.map((license) => license.id),
                            );
                          } else {
                            setSelectedLicenses([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Claimed?</TableCell>
                    <TableCell>Expires At</TableCell>
                    <TableCell>Revoked at</TableCell>
                    <TableCell>
                      <TableSortLabel {...tableSortLabelProps('createdAt')}>
                        Created at
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>Diagnosis</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licences.map((license) => {
                    const isSelected = selectedLicenses.includes(license.id);
                    return (
                      <TableRow key={license.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedLicenses([
                                  ...selectedLicenses,
                                  license.id,
                                ]);
                              } else {
                                setSelectedLicenses(
                                  selectedLicenses.filter(
                                    (id) => id !== license.id,
                                  ),
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {license.code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={license.claim ? 'Yes' : 'No'}
                            color={license.claim ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {license.expiresAt ? (
                            <Chip
                              label={format(license.expiresAt, 'dd MMM yyyy')}
                              color={
                                parseISO(license.expiresAt) < new Date()
                                  ? 'error'
                                  : 'success'
                              }
                            />
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {license.revokedAt ? (
                            <Chip
                              label={format(license.revokedAt, 'dd MMM yyyy')}
                              color="error"
                            />
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {license.createdAt
                              ? format(license.createdAt, 'dd MMM yyyy')
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {license.claim?.user?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {license.claim?.user?.diagnoses
                              ?.map((d) => Model.User.getDiagnosisLabel(d))
                              .join(', ') || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              setAnchorEl(event.currentTarget);
                              setSelectedLicenseId(license.id);
                            }}
                          >
                            <DotsThreeVertical size={24} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Box sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                <TablePagination
                  {...tablePaginationProps({
                    totalItems: licencesApi.data?.totalItems ?? 0,
                    rowsPerPage: 10,
                  })}
                  count={licencesApi.data?.totalItems ?? 0}
                />
              </Box>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No licenses created yet
              </Typography>
            </Box>
          )}
        </Card>

        <NewTeamModal
          open={teamModalOpen}
          onClose={() => setTeamModalOpen(false)}
          organisationName={organisation.name}
          organisationId={organisation.id}
          existingTeam={team}
          onSave={() => teamsApi.invalidate()}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleRevoke}>
            <ListItemText>Revoke Licence</ListItemText>
          </MenuItem>
          {!programHasStarted && (
            <MenuItem onClick={handleMove}>
              <ListItemText>Move to other Team</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleEdit}>
            <ListItemText>Edit Expiry Date</ListItemText>
          </MenuItem>
        </Menu>

        <Dialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              Create Licences for {organisation.name} {team.name}
              <IconButton onClick={() => setCreateModalOpen(false)}>
                <X size={24} />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Number of licences"
                value={numberOfLicences}
                onChange={(e) => setNumberOfLicences(e.target.value)}
                fullWidth
                inputProps={{ min: 1 }}
                type="number"
                required
              />
              <TextField
                label="Licences expire at"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                fullWidth
                type="date"
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="contained"
              onClick={handleCreateLicences}
              disabled={
                !numberOfLicences || parseInt(numberOfLicences, 10) <= 0
              }
            >
              Generate Licences
            </Button>
            <Button
              variant="outlined"
              onClick={() => setCreateModalOpen(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              Licences generated
              <IconButton onClick={() => setSuccessModalOpen(false)}>
                <X size={24} />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                The licences have been successfully generated for{' '}
                {organisation.name} Team {team.name} ({team.coach.name}, 20 Mar)
              </Typography>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Generated Codes
                </Typography>
                {generatedCodes.map((code, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', mb: 0.5 }}
                  >
                    {code}
                  </Typography>
                ))}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Copy />}
              onClick={() =>
                navigator.clipboard.writeText(generatedCodes.join('\n'))
              }
            >
              Copy Codes
            </Button>
          </DialogActions>
        </Dialog>

        <MoveLicencesModal
          open={moveModalOpen}
          onClose={() => {
            setMoveModalOpen(false);
            setPendingLicenceIds([]);
            setSelectedTeam('');
          }}
          teams={teamsApi.data.member || []}
          currentTeamId={teamId}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          disableSave={!selectedTeam || pendingLicenceIds.length === 0}
          onSave={async () => {
            const ids = pendingLicenceIds;
            if (ids.length === 0 || !selectedTeam) return;
            const res = await move(ids, selectedTeam);
            if (res) {
              setSelectedLicenses([]);
              setSelectedLicenseId(null);
              setSelectedTeam('');
              setPendingLicenceIds([]);
              setMoveModalOpen(false);
            }
          }}
        />

        <EditExpiryModal
          open={editExpiryModalOpen}
          onClose={() => {
            setEditExpiryModalOpen(false);
            setPendingLicenceIds([]);
          }}
          expiryDate={expiryDate}
          setExpiryDate={setExpiryDate}
          onSave={async () => {
            const ids = pendingLicenceIds;
            if (!expiryDate || ids.length === 0) return;
            const res = await setExpiry(ids, expiryDate);
            if (res) {
              setSelectedLicenses([]);
              setSelectedLicenseId(null);
              setEditExpiryModalOpen(false);
              setPendingLicenceIds([]);
            }
          }}
        />
      </Stack>
    </ScreenWrap>
  );
}
